'use client';
import { Button } from '@/src/features/ui/button/button';
import { RelLink } from '@/src/features/ui/rel-link';
import RoundedContainer from '@/src/features/ui/rounded-container';
import Tooltip from '@/src/features/ui/tooltip/tooltip';
import { iletisimFormValidationSchema, subjectOptionList } from './iletisim-form-data-validations';
import { getCaptchaToken } from '@/src/utils/captcha';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CaptchaType } from '@/src/interfaces/data-contracts';
import InputField from '@/src/features/ui/react-hook-form/ui/input-field';
import { useMemo, useState } from 'react';
import { insertCommentAction } from './actions';
import { AlertCircleIcon, AngledArrowDownIcon } from '@/src/features/ui/icons/icons';

const IletisimForm: React.FC = () => {
  const form = useForm<z.infer<typeof iletisimFormValidationSchema>>({
    resolver: zodResolver(iletisimFormValidationSchema),
    mode: 'all',
  });
  type RequestStatus = 'initial' | 'pending' | 'success' | 'error';
  const [request, setRequest] = useState<{ status: RequestStatus; data?: unknown; error?: unknown }>({
    status: 'initial',
  });
  const onSubmit = async (values: any) => {
    const selectedSubject = subjectOptionList.find((option) => option.value === Number(values.subject));
    const token = await getCaptchaToken('contactform');
    try {
      setRequest({ status: 'pending' });
      const response = await insertCommentAction({
        subject: values.subject,
        subjectTitle: selectedSubject ? selectedSubject.label : '',
        message: values.message,
        name: values.name,
        surname: values.surname,
        email: values.email,
        contactChecked: values.acikrizaChecked,
        version: CaptchaType.V3,
        token,
      });
      setRequest({ status: 'success', data: response });
      form.reset();
    } catch (error) {
      setRequest({ status: 'error', error });
    }
  };

  const inputFieldClassNames = useMemo(
    () => ({
      overrideInputClassName:
        'bg-white border border-solid rounded-lg text-black700 py-[15px] px-4 md:px-5 w-full font-semibold text-sm leading-5 md:text-base md:leading-6 appearance-none',
      overrideLabelClassName: 'text-xs font-semibold leading-[18px] text-black700 md:text-sm md:leading-5',
      overrideErrorClassName: 'text-xs font-semibold leading-4 text-error flex items-center justify-start mt-2',
      overrideInputErrorClassName: 'border-error',
      errorIcon: <AlertCircleIcon className="mr-2 h-4 w-4" />,
    }),
    []
  );

  return (
    <RoundedContainer noShadow data-testid="contactForm">
      <h2 className="text-xl md:text-2xl" data-testid="title">
        Bize Ulaşın
      </h2>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mt-6 flex flex-col gap-5">
            <div className="flex flex-col gap-y-5 md:flex md:flex-row md:gap-x-5">
              <InputField label="Ad" name="name" placeholder={'Adınızı Giriniz'} {...inputFieldClassNames} />

              <InputField
                label="Soyadınız"
                name="surname"
                placeholder={'Soyadınızı Giriniz'}
                {...inputFieldClassNames}
              />
            </div>
            <InputField label="E-Posta" name="email" placeholder={'E-Posta adresiniz'} {...inputFieldClassNames} />

            <div className="relative">
              <InputField
                type="select"
                label="Konu"
                name="subject"
                showNoneOption={'Seçiniz'}
                {...inputFieldClassNames}
              >
                {subjectOptionList.map((item) => (
                  <option key={item.value} value={item.value} data-testid="subjectOption">
                    {item.label}
                  </option>
                ))}
              </InputField>
              <AngledArrowDownIcon
                className="pointer-events-auto absolute right-5 top-1/2 translate-y-1/4"
                width="16px"
                height="16px"
              />
            </div>

            <InputField
              type="textarea"
              label="Mesajınız"
              name="message"
              placeholder={'Yazınız...'}
              overrideInputClassName="h-36 bg-white border border-solid rounded-lg text-black700 py-[15px] px-4 md:px-5 w-full font-semibold text-sm leading-5 md:leading-6 appearance-none"
              overrideLabelClassName="text-xs font-semibold leading-[18px] text-black700 md:text-sm md:leading-5"
              overrideErrorClassName="text-xs font-semibold leading-4 text-error flex items-center justify-start mt-2"
              errorIcon={<AlertCircleIcon className="mr-2 h-4 w-4" />}
            />

            <div className="flex w-full items-center">
              <label htmlFor="acikrizaChecked" data-testid="aydinlatmaCheckedLabel">
                <div className="mb-2 text-xs font-semibold leading-[18px] text-black700 md:mb-2 md:text-sm md:leading-5"></div>
                <div className="relative">
                  <input
                    id="acikrizaChecked"
                    type="checkbox"
                    {...form.register('acikrizaChecked')}
                    className="mr-2 h-5 w-5 cursor-pointer bg-none"
                    data-testid="acikrizaChecked"
                  />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-5"></div>
                </div>
              </label>
              <div className="relative"></div>
              <div>
                <span className="text-sm text-black800">
                  HangiKredi Kobi{' '}
                  <strong className="underline">
                    <RelLink href="/hakkimizda/acik-riza-metni" target={'_blank'}>
                      Açık Rıza Metni
                    </RelLink>
                  </strong>
                  {/* */}
                  ’ni okudum ve açık rıza metni kapsamında belirtilen kişisel verilerimin işlenmesine onay veriyorum.
                </span>
              </div>
            </div>

            <div className="flex w-full items-center">
              <label htmlFor="aydinlatmaChecked" data-testid="aydinlatmaCheckedLabel">
                <div className="mb-2 text-xs font-semibold leading-[18px] text-black700 md:mb-2 md:text-sm md:leading-5"></div>
                <div className="relative">
                  <input
                    id="aydinlatmaChecked"
                    type="checkbox"
                    {...form.register('aydinlatmaChecked')}
                    className="mr-2 h-5 w-5 cursor-pointer bg-none"
                    data-testid="aydinlatmaChecked"
                  />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-5"></div>
                </div>
              </label>
              <div className="relative"></div>
              <div>
                <span className="text-xs text-black800 md:text-sm">
                  HangiKredi Kobi{' '}
                  <strong className="underline">
                    <RelLink
                      href="/hakkimizda/aydinlatma-metni-musteri"
                      target={'_blank'}
                      data-testid="clarificationTextLink"
                    >
                      Aydınlatma Metni
                    </RelLink>
                    {/* */}
                  </strong>
                  {/**/}
                  ’ni okudum.
                </span>
              </div>
            </div>

            <div className="flex w-full items-center">
              <label htmlFor="contactChecked" data-testid="aydinlatmaCheckedLabel">
                <div className="mb-2 text-xs font-semibold leading-[18px] text-black700 md:mb-2 md:text-sm md:leading-5"></div>
                <div className="relative">
                  <input
                    id="contactChecked"
                    type="checkbox"
                    {...form.register('contactChecked')}
                    className="mr-2 h-5 w-5 cursor-pointer bg-none"
                    data-testid="contactChecked"
                  />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-5"></div>
                </div>
              </label>
              <div className="relative"></div>
              <div>
                <span className="text-sm text-black800">
                  HangiKredi’nin ticari elektronik ileti göndermesini{''}
                  <strong className="underline">
                    <Tooltip tooltipType="text" tooltipText="onay metni" className="ml-1" position="right">
                      Paylaştığım verilerin tarafıma ticari elektronik ileti gönderilmesi amacıyla Aydınlatma Metni
                      kapsamında işlenmesine onay veriyorum.
                    </Tooltip>
                  </strong>{' '}
                  kapsamında kabul ediyorum.
                </span>
              </div>
            </div>

            <Button
              text="Gönder"
              className="h-12"
              type="submit"
              disabled={!form.formState.isValid}
              data-testid="sendButton"
            />
            {request.status === 'pending' && <div data-testid="sendingMessage">Gönderiliyor...</div>}
            {request.status === 'success' && <div data-testid="successMessage">Mesajınız başarıyla gönderildi.</div>}
            {request.status === 'error' && (
              <div className="mt-2 text-red-600" data-testid="staticErrorMessage">
                Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz.
                {request.error &&
                typeof request.error === 'object' &&
                'data' in request.error &&
                Array.isArray(request.error?.data) &&
                request.error.data.length > 0 ? (
                  <div>
                    <ul className="ml-5 list-disc" data-testid="errorMessageList">
                      {request.error.data.map((err, index) => (
                        <li key={index} data-testid="errorMessage">
                          {err.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </RoundedContainer>
  );
};

export default IletisimForm;
